import { API, BACKEND_URL } from '@/config/api';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Create a new location
 */
export const createNewLocationHelper = async ({
  newLocationLetter,
  newLocationName,
  newLocationAddress,
  newLocationLatitude,
  newLocationLongitude,
  newLocationAreaId,
  allAvailableLocations,
  gridData,
  zooplanktonGridData,
  pendingColumnId,
  setLoading,
  setAllAvailableLocations,
  setCurrentLocations,
  setGridData,
  setZooplanktonGridData,
  selectLocationForColumn,
  setPendingColumnId,
  resetLocationForm,
  setShowNewLocationModal,
  isDemo = false
}) => {
  if (!newLocationLetter.trim() || !newLocationName.trim()) {
    toast.error('Vennligst fyll ut både bokstav og navn');
    return;
  }
  
  if (!newLocationAreaId) {
    toast.error('Vennligst velg et område');
    return;
  }
  
  // Create new location string
  const newLocationFullName = `${newLocationLetter.toUpperCase()} - ${newLocationName.trim()}`;
  
  // Check if location already exists
  const existingLocation = allAvailableLocations.find(
    loc => `${loc.letter} - ${loc.name}` === newLocationFullName
  );
  
  if (existingLocation) {
    toast.error('Denne lokaliteten eksisterer allerede');
    return;
  }
  
  try {
    setLoading(true);
    
    if (isDemo) {
      // Demo mode - just add locally
      const newLoc = {
        id: `loc-${newLocationLetter.toLowerCase()}`,
        letter: newLocationLetter.toUpperCase(),
        name: newLocationName.trim(),
        address: newLocationAddress,
        latitude: newLocationLatitude ? parseFloat(newLocationLatitude) : null,
        longitude: newLocationLongitude ? parseFloat(newLocationLongitude) : null,
        area_id: newLocationAreaId
      };
      
      setAllAvailableLocations(prev => [...prev, newLoc]);
      
      // Add to currentLocations
      setCurrentLocations(prev => [...prev, newLocationFullName]);
      
      // Initialize grid data for all organisms for this new location
      const newGridData = { ...gridData };
      Object.keys(newGridData).forEach(key => {
        newGridData[key][newLocationFullName] = '';
      });
      setGridData(newGridData);
      
      // Initialize zooplankton grid data
      const newZooplanktonData = { ...zooplanktonGridData };
      Object.keys(newZooplanktonData).forEach(key => {
        newZooplanktonData[key][newLocationFullName] = '';
      });
      setZooplanktonGridData(newZooplanktonData);
      
      // If there's a pending column, assign the new location to it
      if (pendingColumnId) {
        selectLocationForColumn(pendingColumnId, newLocationFullName);
        setPendingColumnId(null);
      }
      
      // Reset form and close modal
      resetLocationForm();
      setShowNewLocationModal(false);
      
      toast.success(`Lokalitet ${newLocationLetter.toUpperCase()} - ${newLocationName.trim()} opprettet!`);
      return;
    }
    
    // Real mode - save to backend
    const response = await axios.post(`${API}/locations`, {
      letter: newLocationLetter.toUpperCase(),
      name: newLocationName.trim(),
      address: newLocationAddress,
      latitude: newLocationLatitude ? parseFloat(newLocationLatitude) : null,
      longitude: newLocationLongitude ? parseFloat(newLocationLongitude) : null,
      area_id: newLocationAreaId
    }, { withCredentials: true });
    
    // Add to local state
    setAllAvailableLocations(prev => [...prev, response.data]);
    
    // Add to currentLocations
    setCurrentLocations(prev => [...prev, newLocationFullName]);
    
    // Initialize grid data for all organisms for this new location
    const newGridData = { ...gridData };
    Object.keys(newGridData).forEach(key => {
      newGridData[key][newLocationFullName] = '';
    });
    setGridData(newGridData);
    
    // Initialize zooplankton grid data
    const newZooplanktonData = { ...zooplanktonGridData };
    Object.keys(newZooplanktonData).forEach(key => {
      newZooplanktonData[key][newLocationFullName] = '';
    });
    setZooplanktonGridData(newZooplanktonData);
    
    // If there's a pending column, assign the new location to it
    if (pendingColumnId) {
      selectLocationForColumn(pendingColumnId, newLocationFullName);
      setPendingColumnId(null);
    }
    
    // Reset form and close modal
    resetLocationForm();
    setShowNewLocationModal(false);
    
    toast.success(`Lokalitet ${newLocationLetter.toUpperCase()} - ${newLocationName.trim()} opprettet!`);
    
  } catch (error) {
    console.error('Error creating location:', error);
    toast.error('Kunne ikke opprette lokalitet');
  } finally {
    setLoading(false);
  }
};

/**
 * Select location for a dynamic column
 */
export const selectLocationForColumnHelper = ({
  columnId,
  locationFullName,
  organisms,
  gridData,
  calculateSums,
  setPendingColumnId,
  setShowNewLocationModal,
  setDynamicColumns,
  setColumnSelectMode,
  setGridData
}) => {
  // Check if user wants to add new location
  if (locationFullName === '__NEW__') {
    setPendingColumnId(columnId);
    setShowNewLocationModal(true);
    return;
  }
  
  setDynamicColumns(prev => 
    prev.map(col => col.id === columnId ? { ...col, location: locationFullName } : col)
  );
  setColumnSelectMode(prev => ({ ...prev, [columnId]: false }));
  
  // Initialize gridData for this new column
  const newGridData = { ...gridData };
  organisms.forEach(org => {
    const key = `${org.group}_${org.species}`;
    if (!newGridData[key]) newGridData[key] = {};
    newGridData[key][locationFullName] = '';
  });
  setGridData(calculateSums(newGridData));
  
  toast.success('Kolonne lagt til!');
};

/**
 * Remove a dynamic column
 */
export const removeDynamicColumnHelper = ({
  columnId,
  dynamicColumns,
  gridData,
  setGridData,
  setDynamicColumns,
  setOpenLocationPopover,
  setColumnSelectMode
}) => {
  const column = dynamicColumns.find(c => c.id === columnId);
  
  if (!column) {
    return;
  }
  
  // If column has a location, remove from gridData
  if (column.location) {
    const newGridData = { ...gridData };
    Object.keys(newGridData).forEach(key => {
      delete newGridData[key][column.location];
    });
    setGridData(newGridData);
  }
  
  // Remove column from dynamicColumns
  setDynamicColumns(prev => prev.filter(c => c.id !== columnId));
  
  // Close any open popovers
  setOpenLocationPopover(prev => ({ ...prev, [columnId]: false }));
  setColumnSelectMode(prev => ({ ...prev, [columnId]: false }));
  
  toast.success('Kolonne fjernet');
};

/**
 * Remove a standard column
 */
export const removeStandardColumnHelper = ({
  locationName,
  gridData,
  zooplanktonGridData,
  setCurrentLocations,
  setGridData,
  setZooplanktonGridData
}) => {
  if (!window.confirm(`Fjern "${locationName}" fra skjemaet?`)) return;
  
  // Remove from currentLocations
  setCurrentLocations(prev => prev.filter(loc => loc !== locationName));
  
  // Remove from gridData
  const newGridData = { ...gridData };
  Object.keys(newGridData).forEach(key => {
    delete newGridData[key][locationName];
  });
  setGridData(newGridData);
  
  // Remove from zooplankton
  const newZooplanktonData = { ...zooplanktonGridData };
  Object.keys(newZooplanktonData).forEach(key => {
    delete newZooplanktonData[key][locationName];
  });
  setZooplanktonGridData(newZooplanktonData);
  
  toast.success('Kolonne fjernet');
};
